import React from 'react';
import { Modal } from '../../ui/common';

/**
 * Lifecycle of the item being shown inside the shell.
 *
 * - `ready`: render the provider adapter's body/footer normally.
 * - `loading`: hydration in flight; body/footer are not rendered yet.
 * - `stale`: the deep-linked item disappeared (e.g. background refresh);
 *   the shell shows a bounded unavailable message instead of an empty
 *   detail surface.
 * - `error`: hydration failed; the shell offers a retry without discarding
 *   whatever content is still available.
 */
export type ProviderItemDetailStatus = 'ready' | 'loading' | 'stale' | 'error';

export interface ProviderItemDetailProps {
  isOpen: boolean;
  onClose: () => void;
  /** Accessible label for the dialog; typically includes the provider identity. */
  ariaLabel: string;
  /** Root `data-testid`; defaults to `provider-item-detail`. */
  testId?: string;
  /**
   * Sticky header content. Provider adapters render their own icon, stable
   * identity, title, close control, and safe external-open action here —
   * the shell only owns the surrounding chrome (position, focus, dismissal).
   */
  header: React.ReactNode;
  /** Scrollable body owned by the detail surface. Ignored unless `status` is `ready`. */
  children?: React.ReactNode;
  /** Sticky/reachable action or composer region, rendered below the body. */
  footer?: React.ReactNode;
  status?: ProviderItemDetailStatus;
  loadingLabel?: string;
  staleLabel?: string;
  errorLabel?: string;
  onRetry?: () => void;
  closeOnBackdropClick?: boolean;
}

const DEFAULT_STALE_LABEL = 'Este elemento ya no está disponible.';
const DEFAULT_ERROR_LABEL = 'No se pudo cargar el detalle de este ítem.';
const DEFAULT_LOADING_LABEL = 'Cargando detalle…';

/**
 * Shared full-detail shell for provider items (`design.md` — "Complete
 * detail"). Desktop renders a large overlay clearly distinct from the right
 * context sidebar; on narrow layouts `Modal`'s `fullscreenOnMobile` behavior
 * turns this into an explicit full-detail sheet below the persistent
 * workspace header. The shell owns focus containment, Escape/back
 * dismissal, and scroll lock (via `Modal`); provider adapters own their own
 * header extras, body content, and footer actions.
 */
export const ProviderItemDetail: React.FC<ProviderItemDetailProps> = ({
  isOpen,
  onClose,
  ariaLabel,
  testId = 'provider-item-detail',
  header,
  children,
  footer,
  status = 'ready',
  loadingLabel = DEFAULT_LOADING_LABEL,
  staleLabel = DEFAULT_STALE_LABEL,
  errorLabel = DEFAULT_ERROR_LABEL,
  onRetry,
  closeOnBackdropClick = true,
}) => {
  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="full"
      showCloseButton={false}
      ariaLabel={ariaLabel}
      closeOnBackdropClick={closeOnBackdropClick}
    >
      <div
        data-testid={testId}
        style={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100%',
          maxHeight: '100%',
          color: 'var(--z-text)',
          fontFamily: 'var(--font-sans)',
        }}
      >
        <div
          data-testid={`${testId}-header`}
          style={{ position: 'sticky', top: 0, zIndex: 1, flex: '0 0 auto' }}
        >
          {header}
        </div>

        <div
          data-testid={`${testId}-body`}
          style={{
            flex: '1 1 auto',
            minHeight: 0,
            overflowY: 'auto',
          }}
        >
          {status === 'loading' ? (
            <div
              data-testid={`${testId}-loading`}
              role="status"
              aria-live="polite"
              style={{ padding: 32, textAlign: 'center', color: 'var(--z-dim)', fontSize: 13 }}
            >
              {loadingLabel}
            </div>
          ) : status === 'stale' ? (
            <div
              data-testid={`${testId}-stale`}
              role="status"
              style={{ padding: 32, textAlign: 'center', color: 'var(--z-dim)', fontSize: 13 }}
            >
              <p style={{ margin: '0 0 12px' }}>{staleLabel}</p>
              <button
                type="button"
                data-testid={`${testId}-return-to-feed`}
                onClick={onClose}
                style={{
                  border: 'none',
                  borderRadius: 8,
                  padding: '8px 16px',
                  background: 'var(--z-fill-2)',
                  color: 'var(--z-text)',
                  cursor: 'pointer',
                  fontSize: 12.5,
                  fontWeight: 600,
                }}
              >
                Volver al feed
              </button>
            </div>
          ) : status === 'error' ? (
            <div
              data-testid={`${testId}-error`}
              role="alert"
              style={{ padding: 32, textAlign: 'center', color: 'var(--z-bad)', fontSize: 13 }}
            >
              <p style={{ margin: '0 0 12px' }}>{errorLabel}</p>
              {onRetry ? (
                <button
                  type="button"
                  data-testid={`${testId}-retry`}
                  onClick={onRetry}
                  style={{
                    border: 'none',
                    borderRadius: 8,
                    padding: '8px 16px',
                    background: 'var(--z-fill-2)',
                    color: 'var(--z-text)',
                    cursor: 'pointer',
                    fontSize: 12.5,
                    fontWeight: 600,
                  }}
                >
                  Reintentar
                </button>
              ) : null}
            </div>
          ) : (
            children
          )}
        </div>

        {status === 'ready' && footer ? (
          <div
            data-testid={`${testId}-footer`}
            style={{ position: 'sticky', bottom: 0, flex: '0 0 auto' }}
          >
            {footer}
          </div>
        ) : null}
      </div>
    </Modal>
  );
};
